import React, { useState, useEffect, useCallback } from "react";
import { Star, MessageCircle, Edit3, Trash2, Send } from "lucide-react";
import {
  getListingReviews,
  getProfileReviews,
  getListingReviewEligibility,
  getProfileReviewEligibility,
  createListingReview,
  createProfileReview,
  updateReview,
  deleteReview,
  replyToReview,
  MarketplaceReview,
  CreateReviewPayload,
  ReviewEligibility,
} from "../../../Redux/ApiCalls/Dashboard/MarketplaceAPI";
import { showSuccessToast, showErrorToast } from "../../atoms/Utlis/Toast";

import {
  ReviewsContainer,
  ReviewsHeader,
  RatingSummary,
  BigRating,
  RatingInfo,
  StarRow,
  ReviewCountText,
  WriteReviewBtn,
  WriteReviewFormContainer,
  FormLabel,
  StarPickerRow,
  TextInput,
  TextArea,
  FormActions,
  CancelBtn,
  ReviewsList,
  ReviewCard,
  ReviewerMeta,
  ReviewerAvatar,
  ReviewerInfo,
  ReviewerName,
  ReviewDate,
  ReviewTitle,
  ReviewBodyText,
  OwnerReplyBlock,
  OwnerReplyHeader,
  OwnerReplyText,
  ReviewActionsRow,
  TextBtn,
  LoadMoreRow,
  LoadMoreBtn,
  EmptyReviews,
  EmptyEmoji,
  EmptyTitle,
  EmptyDesc,
} from "./MarketplaceReviewsSectionStyles";

interface MarketplaceReviewsSectionProps {
  mode: "listing" | "profile";
  targetId: string | number;
  avgRating?: number;
  reviewCount?: number;
  isOwner?: boolean;
  listingGroup?: "equipment" | "services";
  equipmentAvgRating?: number;
  equipmentReviewCount?: number;
  servicesAvgRating?: number;
  servicesReviewCount?: number;
}

const StarRating = ({ rating, size = 14, color = "#ffb400" }: { rating: number, size?: number, color?: string }) => {
  return (
    <StarRow>
      {[1, 2, 3, 4, 5].map(star => {
        const fill = star <= rating ? color : "none";
        const stroke = star <= rating ? color : "#d1d5db";
        // Simple half star logic: if rating is 4.5, star 5 gets half filled.
        // For simplicity with Lucide React Star, we will just use filled/unfilled.
        // If rating is >= star - 0.5, treat as filled.
        const isFilled = rating >= star - 0.5;
        return (
          <Star
            key={star}
            size={size}
            fill={isFilled ? color : "none"}
            color={isFilled ? color : "#d1d5db"}
          />
        );
      })}
    </StarRow>
  );
};

export const MarketplaceReviewsSection: React.FC<MarketplaceReviewsSectionProps> = ({
  mode,
  targetId,
  avgRating = 0,
  reviewCount = 0,
  isOwner = false,
  listingGroup,
  equipmentAvgRating,
  equipmentReviewCount,
  servicesAvgRating,
  servicesReviewCount,
}) => {
  const [reviews, setReviews] = useState<MarketplaceReview[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [filterType, setFilterType] = useState<"all" | "hire" | "services" | "equipment">("all");
  const [apiSummary, setApiSummary] = useState<{avg_rating: number; review_count: number} | null>(null);

  const [eligibility, setEligibility] = useState<ReviewEligibility>({ can_review: false });
  const [checkingEligibility, setCheckingEligibility] = useState(false);

  const [showForm, setShowForm] = useState(false);
  const [formState, setFormState] = useState<CreateReviewPayload & { id?: string }>({ rating: 0, title: "", body: "" });
  const [submitting, setSubmitting] = useState(false);

  const [replyingTo, setReplyingTo] = useState<string | null>(null);
  const [replyText, setReplyText] = useState("");

  const isLoggedIn = !!localStorage.getItem("access_token");

  const fetchReviews = useCallback(async (pg: number, append = false) => {
    if (pg === 1) setLoading(true);
    else setLoadingMore(true);

    try {
      let filterParams = {};
      if (mode === "profile" && filterType !== "all") {
        if (filterType === "hire") filterParams = { target: "hire" };
        else filterParams = { target: "listing", listing_group: filterType };
      }

      const params = { page: pg, page_size: 10, ...(listingGroup ? { target: "listing" as const, listing_group: listingGroup } : {}), ...filterParams };
      const res = mode === "listing"
        ? await getListingReviews(String(targetId), params)
        : await getProfileReviews(targetId, params);

      setReviews(prev => append ? [...prev, ...(res.results || [])] : (res.results || []));
      setHasMore(!!res.next);
      if (!append && res.summary) {
        setApiSummary({ avg_rating: res.summary.avg_rating, review_count: res.summary.review_count });
      }
    } catch (e) {
      if (!append) {
        setReviews([]);
        setApiSummary(null);
      }
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [mode, targetId, listingGroup, filterType]);

  useEffect(() => {
    fetchReviews(1);
  }, [fetchReviews]);

  useEffect(() => {
    if (isLoggedIn && !isOwner) {
      setCheckingEligibility(true);
      const req = mode === "listing"
        ? getListingReviewEligibility(String(targetId))
        : getProfileReviewEligibility(targetId);
      
      req.then(res => setEligibility(res))
         .catch(() => setEligibility({ can_review: false, reason: "Error checking eligibility" }))
         .finally(() => setCheckingEligibility(false));
    }
  }, [mode, targetId, isLoggedIn, isOwner]);

  const handleWriteClick = () => {
    if (!isLoggedIn) {
      window.dispatchEvent(new CustomEvent("open-marketplace-login"));
      return;
    }
    if (eligibility.existing_review_id) {
      const existing = reviews.find(r => r.id === eligibility.existing_review_id);
      if (existing) {
        setFormState({ id: existing.id, rating: existing.rating, title: existing.title || "", body: existing.body || "" });
      } else {
        // Fallback if not loaded
        setFormState({ id: eligibility.existing_review_id, rating: 5, title: "", body: "" });
      }
    } else {
      setFormState({ rating: 0, title: "", body: "" });
    }
    setShowForm(true);
  };

  const handleFormSubmit = async () => {
    if (formState.rating === 0) {
      showErrorToast("Please select a rating");
      return;
    }
    setSubmitting(true);
    try {
      if (formState.id) {
        await updateReview(formState.id, formState);
        showSuccessToast("Review updated successfully!");
      } else {
        if (mode === "listing") {
          await createListingReview(String(targetId), formState);
        } else {
          await createProfileReview(targetId, formState);
        }
        showSuccessToast("Review submitted successfully!");
        setEligibility(prev => ({ ...prev, can_review: false, existing_review_id: "refresh_needed" }));
      }
      setShowForm(false);
      fetchReviews(1); // Refresh list
    } catch (err: any) {
      showErrorToast(err.response?.data?.message || "Failed to submit review");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm("Are you sure you want to delete this review?")) return;
    try {
      await deleteReview(id);
      showSuccessToast("Review deleted");
      setReviews(prev => prev.filter(r => r.id !== id));
      setEligibility(prev => ({ ...prev, can_review: true, existing_review_id: undefined }));
    } catch {
      showErrorToast("Failed to delete review");
    }
  };

  const handleReplySubmit = async (id: string) => {
    if (!replyText.trim()) return;
    setSubmitting(true);
    try {
      await replyToReview(id, { seller_reply: replyText });
      showSuccessToast("Reply posted!");
      setReplyingTo(null);
      setReplyText("");
      setReviews(prev => prev.map(r => r.id === id ? { ...r, seller_reply: replyText } : r));
    } catch {
      showErrorToast("Failed to post reply");
    } finally {
      setSubmitting(false);
    }
  };

  const timeAgo = (dateStr: string) => {
    const diff = (new Date().getTime() - new Date(dateStr).getTime()) / 1000;
    if (diff < 60) return "Just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 2592000) return `${Math.floor(diff / 86400)}d ago`;
    return new Date(dateStr).toLocaleDateString();
  };

  const displayAvgRating = apiSummary ? apiSummary.avg_rating : (filterType === "equipment" && equipmentAvgRating !== undefined ? equipmentAvgRating 
                         : filterType === "services" && servicesAvgRating !== undefined ? servicesAvgRating 
                         : avgRating);
  const displayReviewCount = apiSummary ? apiSummary.review_count : (filterType === "equipment" && equipmentReviewCount !== undefined ? equipmentReviewCount 
                           : filterType === "services" && servicesReviewCount !== undefined ? servicesReviewCount 
                           : reviewCount);

  return (
    <ReviewsContainer>
      {/* ── HEADER & SUMMARY ── */}
      <ReviewsHeader>
        <RatingSummary>
          <BigRating>{Number(displayAvgRating).toFixed(1)}</BigRating>
          <RatingInfo>
            <StarRating rating={displayAvgRating} size={18} />
            <ReviewCountText>{displayReviewCount} review{displayReviewCount !== 1 ? "s" : ""}</ReviewCountText>
          </RatingInfo>
        </RatingSummary>

        {!isOwner && (
          <WriteReviewBtn 
            onClick={handleWriteClick}
            disabled={checkingEligibility || (!eligibility.can_review && !eligibility.existing_review_id && isLoggedIn)}
            title={!isLoggedIn ? "Login to review" : !eligibility.can_review && !eligibility.existing_review_id ? (eligibility.reason || "Not eligible") : eligibility.existing_review_id ? "Edit your review" : "Write a review"}
          >
            {eligibility.existing_review_id ? "Edit Your Review" : "Write a Review"}
          </WriteReviewBtn>
        )}
      </ReviewsHeader>

      {mode === "profile" && (
        <div style={{ display: "flex", gap: "8px", marginBottom: "16px", flexWrap: "wrap" }}>
          {(["all", "hire", "services", "equipment"] as const).map((type) => (
            <button
              key={type}
              onClick={() => { setFilterType(type); setPage(1); }}
              style={{
                padding: "6px 14px",
                borderRadius: "100px",
                border: filterType === type ? "none" : "1.5px solid rgba(255,255,255,0.2)",
                background: filterType === type ? "var(--mp-iconGrad, linear-gradient(135deg, #FFC951 0%, #C94DAA 100%))" : "transparent",
                color: filterType === type ? "#fff" : "#fff",
                fontSize: "12px",
                fontWeight: 700,
                cursor: "pointer",
                textTransform: "capitalize",
                fontFamily: "Montserrat, sans-serif"
              }}
            >
              {type}
            </button>
          ))}
        </div>
      )}

      {/* ── WRITE FORM ── */}
      {showForm && (
        <WriteReviewFormContainer>
          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            <FormLabel>Your Rating <span style={{ color: "#ef4444" }}>*</span></FormLabel>
            <StarPickerRow>
              {[1, 2, 3, 4, 5].map(star => (
                <Star
                  key={star}
                  size={24}
                  fill={star <= formState.rating ? "#ffb400" : "none"}
                  color={star <= formState.rating ? "#ffb400" : "#d1d5db"}
                  onClick={() => setFormState(p => ({ ...p, rating: star }))}
                  style={{ transition: "all 0.1s" }}
                />
              ))}
            </StarPickerRow>
          </div>
          
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <FormLabel>Title (optional)</FormLabel>
            <TextInput
              placeholder="Summarize your experience"
              value={formState.title}
              onChange={e => setFormState(p => ({ ...p, title: e.target.value }))}
            />
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <FormLabel>Review (optional)</FormLabel>
            <TextArea
              placeholder="Tell others about your experience..."
              value={formState.body}
              onChange={e => setFormState(p => ({ ...p, body: e.target.value }))}
            />
          </div>

          <FormActions>
            <CancelBtn onClick={() => setShowForm(false)}>Cancel</CancelBtn>
            <WriteReviewBtn onClick={handleFormSubmit} disabled={submitting}>
              {submitting ? "Submitting..." : formState.id ? "Update Review" : "Submit Review"}
            </WriteReviewBtn>
          </FormActions>
        </WriteReviewFormContainer>
      )}

      {/* ── REVIEWS LIST ── */}
      {loading ? (
        <div style={{ padding: "40px 0", textAlign: "center", color: "var(--mp-featureDescColor)" }}>Loading reviews...</div>
      ) : reviews.length === 0 ? (
        <EmptyReviews>
          <EmptyEmoji>⭐</EmptyEmoji>
          <EmptyTitle>No reviews yet</EmptyTitle>
          <EmptyDesc>Be the first to share your experience!</EmptyDesc>
        </EmptyReviews>
      ) : (
        <ReviewsList>
          {reviews.map(review => (
            <ReviewCard key={review.id}>
              <ReviewerMeta>
                {review.reviewer_avatar ? (
                  <img src={review.reviewer_avatar} alt={review.reviewer_name} style={{ width: 36, height: 36, borderRadius: "50%", objectFit: "cover" }} />
                ) : (
                  <ReviewerAvatar>{review.reviewer_name.charAt(0).toUpperCase()}</ReviewerAvatar>
                )}
                <ReviewerInfo>
                  <ReviewerName>{review.reviewer_name}</ReviewerName>
                  <ReviewDate>{timeAgo(review.created_at)}</ReviewDate>
                </ReviewerInfo>
              </ReviewerMeta>

              <StarRating rating={review.rating} />

              {(review.title || review.body) && (
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  {review.title && <ReviewTitle>{review.title}</ReviewTitle>}
                  {review.body && <ReviewBodyText>{review.body}</ReviewBodyText>}
                </div>
              )}

              {/* Owner Reply */}
              {review.seller_reply ? (
                <OwnerReplyBlock>
                  <OwnerReplyHeader>
                    <MessageCircle size={14} /> Owner Reply
                  </OwnerReplyHeader>
                  <OwnerReplyText>{review.seller_reply}</OwnerReplyText>
                </OwnerReplyBlock>
              ) : isOwner ? (
                <>
                  {replyingTo === review.id ? (
                    <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 8 }}>
                      <TextArea
                        placeholder="Write your reply..."
                        value={replyText}
                        onChange={e => setReplyText(e.target.value)}
                        style={{ minHeight: 60 }}
                      />
                      <FormActions style={{ marginTop: 0 }}>
                        <CancelBtn onClick={() => { setReplyingTo(null); setReplyText(""); }}>Cancel</CancelBtn>
                        <WriteReviewBtn onClick={() => handleReplySubmit(review.id)} disabled={submitting}>
                          <Send size={14} style={{ marginRight: 6, verticalAlign: "middle" }} /> Reply
                        </WriteReviewBtn>
                      </FormActions>
                    </div>
                  ) : (
                    <ReviewActionsRow>
                      <TextBtn onClick={() => setReplyingTo(review.id)}>Reply</TextBtn>
                    </ReviewActionsRow>
                  )}
                </>
              ) : null}

              {/* Edit/Delete for Mine */}
              {review.is_mine && !showForm && (
                <ReviewActionsRow>
                  <TextBtn onClick={() => {
                    setFormState({ id: review.id, rating: review.rating, title: review.title || "", body: review.body || "" });
                    setShowForm(true);
                  }}>
                    <Edit3 size={12} style={{ marginRight: 4, verticalAlign: "middle" }} /> Edit
                  </TextBtn>
                  <TextBtn onClick={() => handleDelete(review.id)} style={{ color: "#ef4444" }}>
                    <Trash2 size={12} style={{ marginRight: 4, verticalAlign: "middle" }} /> Delete
                  </TextBtn>
                </ReviewActionsRow>
              )}
            </ReviewCard>
          ))}

          {hasMore && (
            <LoadMoreRow>
              <LoadMoreBtn onClick={() => fetchReviews(page + 1, true)} disabled={loadingMore}>
                {loadingMore ? "Loading..." : "Load More Reviews"}
              </LoadMoreBtn>
            </LoadMoreRow>
          )}
        </ReviewsList>
      )}
    </ReviewsContainer>
  );
};
